<!-- components/FleetDataGrid.vue -->
<template>
  <v-card>
    <v-card-title v-if="title" class="d-flex justify-space-between align-center">
      {{ title }}
      <v-btn
        v-if="showMobileToggle"
        icon
        small
        @click="toggleMobileBreakpoint"
        class="d-md-none"
      >
        <v-icon>{{ mobileViewEnabled ? 'mdi-view-grid' : 'mdi-table' }}</v-icon>
      </v-btn>
    </v-card-title>

    <v-data-table
      @item-expanded="onRowExpanded"
      item-key="unitId"
      show-expand
      :expanded.sync="expanded"
      :mobile-breakpoint="effectiveMobileBreakpoint"
      :items-per-page="itemsPerPage"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :headers="computedHeaders"
      :items="rows"
      :loading="loading"
      :footer-props="footerProps"
      class="elevation-1"
    >
      <!-- Custom expand icon -->
      <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
        <v-btn icon @click.stop="expand(!isExpanded)">
          <v-icon large>
            {{ isExpanded ? 'mdi-minus-circle' : 'mdi-plus-circle' }}
          </v-icon>
        </v-btn>
      </template>

      <!-- Delta coloring for violations -->
      <template v-slot:item.dViolations="{ item }">
        <span :class="deltaClass(item.dViolations)">
          {{ item.dViolations }}
        </span>
      </template>

      <!-- Delta coloring for idle time -->
      <template v-slot:item.dIdle="{ item }">
        <span :class="deltaClassFromString(item.dIdle)">
          {{ item.dIdle }}
        </span>
      </template>

      <!-- Delta coloring for speed time -->
      <template v-slot:item.dSpeedTime="{ item }">
        <span :class="deltaClassFromString(item.dSpeedTime)">
          {{ item.dSpeedTime }}
        </span>
      </template>

      <!-- Highlight TOTAL row -->
      <template v-slot:item.unitId="{ item }">
        <strong v-if="item.unitId === 'TOTAL'">{{ item.unitId }}</strong>
        <span v-else>{{ item.RegNumber || item.unitId }}</span>
      </template>

      <!-- Expanded row content -->
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length">
          <v-card flat>
            <div class="sub-table-scroll">
              <v-progress-linear
                v-if="item.detailsLoading"
                indeterminate
              ></v-progress-linear>

              <v-data-table
                v-else
                :mobile-breakpoint="effectiveMobileBreakpoint"
                :headers="detailHeaders"
                :items="item.details || []"
                dense
              >
                <!-- Action button for opening day details -->
                <template v-slot:item.actions="{ item: subItem }">
                  <div style="width: 25px; overflow: hidden;">
                    <v-btn
                      icon
                      small
                      color="primary"
                      @click="$emit('open-day', { detailItem: subItem, parentItem: item })"
                    >
                      <v-icon small>mdi-open-in-new</v-icon>
                    </v-btn>
                  </div>
                </template>

                <!-- Format idle time -->
                <template v-slot:item.Item2[3][1]="{ item }">
                  {{ formatSeconds(item.Item2[3][1]) }}
                </template>

                <!-- Format distance -->
                <template v-slot:item.Item2[1][1]="{ item }">
                  {{ formatToKm(item.Item2[1][1]) }}
                </template>

                <!-- Format speed time -->
                <template v-slot:item.Item2[4][1]="{ item }">
                  {{ formatSeconds(item.Item2[4][1]) }}
                </template>

                <!-- Navigation button for trip navigator -->
                <template v-slot:item.actions2="{ item: subItem }">
                  <div style="width: 25px; overflow: hidden;">
                    <v-btn
                      icon
                      small
                      color="primary"
                      @click="$emit('open-nav', { subItem, parentItem: item })"
                    >
                      <v-icon large>mdi-play-circle</v-icon>
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </div>
          </v-card>
         </td>
      </template>

      <!-- Custom mobile card template (optional) -->
<!--       <template v-slot:mobile-row="{ item }" v-if="useMobileCards">
        <v-card class="mobile-card ma-2" elevation="2">
          <v-card-text>
            <div class="d-flex justify-space-between align-center">
              <strong>{{ item.RegNumber || item.unitId }}</strong>
              <v-btn icon @click="toggleExpand(item)">
                <v-icon>{{ isExpanded(item) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </v-btn>
            </div>
            <v-divider class="my-2"></v-divider>
            <v-row dense>
              <v-col cols="6">
                <div class="text-caption grey--text">Violations</div>
                <div class="text-body-2 font-weight-medium">{{ item.violations }}</div>
                <div :class="deltaClass(item.dViolations)" class="text-caption">
                  {{ item.dViolations }}
                </div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption grey--text">Trips</div>
                <div class="text-body-2 font-weight-medium">{{ item.trips }}</div>
                <div class="text-caption">{{ item.dTrips }}</div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption grey--text">Distance</div>
                <div class="text-body-2 font-weight-medium">{{ item.distance }}</div>
                <div class="text-caption">{{ item.dDistance }}</div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption grey--text">Duration</div>
                <div class="text-body-2 font-weight-medium">{{ item.duration }}</div>
                <div class="text-caption">{{ item.dDuration }}</div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </template>
 -->
    </v-data-table>
  </v-card>
</template>

<script>
export default {
  name: 'FleetDataGrid',

  props: {
    // Data props
    rows: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },

    // Configuration props
    title: {
      type: String,
      default: ''
    },
    itemsPerPage: {
      type: Number,
      default: 5
    },
    /// V 0.10 - 2026-05-20
    //     removed sortBy and sortDesc props as this component was
    // internally modofying sort and causing problems wiotht the prop
    /*     sortBy: {
      type: String,
      default: 'violations'
    },
    sortDesc: {
      type: Boolean,
      default: true
    },
 */
    // Mobile breakpoint configuration
    // Set to 0 to always show full table, 600 for mobile breakpoint, etc.
    mobileBreakpoint: {
      type: [Number, String],
      default: 600, // Standard Vuetify mobile breakpoint
      validator: (value) => {
        return value === 0 || value === '0' || value > 0
      }
    },

    // Allow toggling mobile breakpoint from parent
    enableMobileToggle: {
      type: Boolean,
      default: true
    },

    // Use custom mobile cards instead of standard mobile view
    useMobileCards: {
      type: Boolean,
      default: false
    },

    // Show/hide footer
    showFooter: {
      type: Boolean,
      default: true
    },

    // Custom headers (optional)
    customHeaders: {
      type: Array,
      default: null
    },

    // Detail headers for expanded view
    detailHeaders: {
      type: Array,
      default: () => [
        {
          text: '',
          value: 'actions',
          sortable: false,
          width: '8px',
          minWidth: '15px',
          class: 'status-col px-0',
          cellClass: 'status-col px-0'
        },
        { text: 'Date', value: 'Item1', width: '40px' },
        { text: 'Violations', value: 'Item2[6][1]', width: '25px', class: 'status-col px-0' },
        { text: 'Idle Time', value: 'Item2[3][1]', width: '20px', class: 'status-col px-0' },
        { text: 'Max Speed', value: 'Item2[5][1]', width: '25px' },
        { text: 'Speed Time', value: 'Item2[4][1]', width: '25px' },
        { text: 'Trips', value: 'Item2[0][1]', width: '25px' },
        { text: 'Distance', value: 'Item2[1][1]', width: '45px' },
        {
          text: '',
          value: 'actions2',
          sortable: false,
          width: '8px',
          minWidth: '15px',
          class: 'status-col px-0',
          cellClass: 'status-col px-0'
        }
      ]
    },

    // API endpoint for fetching details (if using default fetch method)
    detailsApiUrl: {
      type: String,
      default: null
    },

    // Base URL for API calls
    baseUrl: {
      type: String,
      default: ''
    },

    // Current week for data fetching
    currentWeek: {
      type: Number,
      default: 0
    }
  },

  data () {
    return {
      sortBy: 'violations',
      sortDesc: true,
      expanded: [],
      localSortBy: this.sortBy,
      localSortDesc: this.sortDesc,
      localRows: [],
      localMobileBreakpoint: this.mobileBreakpoint,
      mobileViewEnabled: false
    }
  },

  computed: {
    effectiveMobileBreakpoint () {
      // If mobile view is toggled on, force mobile view by setting breakpoint high
      if (this.mobileViewEnabled) {
        return 9999
      }
      return this.localMobileBreakpoint
    },

    footerProps () {
      if (!this.showFooter) {
        return { itemsPerPageOptions: [] }
      }
      return {
        showFirstLastPage: true,
        itemsPerPageOptions: [5, 10, 25, 50],
        itemsPerPageText: 'Rows per page'
      }
    },

    computedHeaders () {
      if (this.customHeaders) {
        return this.customHeaders
      }

      // Default headers
      return [
        { text: 'Registration', value: 'unitId', width: '50px' },
        { text: 'Weeks Violations', value: 'violations', width: '25px' },
        { text: 'Change Violations', value: 'dViolations', width: '15px' },
        { text: 'Weeks Idle Time', value: 'idle', width: '25px' },
        { text: 'Change Idle Time', value: 'dIdle', width: '15px' },
        { text: 'Weeks Speed Time', value: 'speedTime', width: '25px' },
        { text: 'Change Speed Time', value: 'dSpeedTime', width: '15px' },
        { text: 'Weeks Duration Drive Time', value: 'duration', width: '25px' },
        { text: 'Change Duration', value: 'dDuration', width: '15px' },
        { text: 'Weeks Distance (km)', value: 'distance', width: '30px' },
        { text: 'Change Distance (km)', value: 'dDistance', width: '15px' },
        { text: 'Weeks Trip Count', value: 'trips', width: '25px' },
        { text: 'Change Trips', value: 'dTrips', width: '15px' }
      ]
    },

    showMobileToggle () {
      return this.enableMobileToggle && this.localMobileBreakpoint !== 0
    }
  },

  watch: {
    rows: {
      handler (newRows) {
        this.localRows = newRows
      },
      immediate: true,
      deep: true
    },
    sortBy (newVal) {
      this.localSortBy = newVal
    },

    sortDesc (newVal) {
      this.localSortDesc = newVal
    },
    mobileBreakpoint (newVal) {
      this.localMobileBreakpoint = newVal
    }
  },

  methods: {
    toggleMobileBreakpoint () {
      if (this.mobileViewEnabled) {
        // Switch to normal view
        this.mobileViewEnabled = false
        this.localMobileBreakpoint = this.mobileBreakpoint
      } else {
        // Force mobile view
        this.mobileViewEnabled = true
      }
      this.$emit('mobile-view-toggled', this.mobileViewEnabled)
    },

    toggleExpand (item) {
      const index = this.expanded.findIndex(i => i.unitId === item.unitId)
      if (index === -1) {
        this.expanded.push(item)
      } else {
        this.expanded.splice(index, 1)
      }
    },

    isExpanded (item) {
      return this.expanded.some(i => i.unitId === item.unitId)
    },

    async onRowExpanded ({ item, value }) {
      if (value && !item.details && !item.detailsLoading) {
        // If custom fetch method provided via prop, use it
        if (this.$listeners['fetch-details']) {
          this.$emit('fetch-details', { item, week: this.currentWeek })
        }
        // Otherwise use default API call if URL provided
        else if (this.detailsApiUrl) {
          await this.fetchDetailsDefault(item)
        }
      }

      // Emit event for parent to handle
      this.$emit('row-expanded', { item, expanded: value })
    },

    async fetchDetailsDefault (item) {
      item.detailsLoading = true

      try {
        const url = `${this.baseUrl}${this.detailsApiUrl}?UnitID=${item.unitId}&Week=${this.currentWeek}`
        const response = await this.$axios.get(url)
        item.details = response.data
        this.$set(item, 'details', response.data)
      } catch (e) {
        console.error('Error loading details', e)
        item.details = []
        this.$set(item, 'details', [])
      } finally {
        item.detailsLoading = false
      }
    },

    formatSeconds (sec) {
      if (!sec && sec !== 0) return '-'
      const h = Math.floor(sec / 3600)
      const m = Math.floor((sec % 3600) / 60)
      return `${h}h ${m}m`
    },

    formatToKm (meters) {
      if (meters == null) return '-'
      const km = (meters / 1000).toFixed(2)
      return `${km} km`
    },

    formatMeters (m) {
      return (m / 1000).toFixed(1) + ' km'
    },

    deltaClass (value) {
      if (value < 0) return 'green--text'
      if (value > 0) return 'red--text'
      return ''
    },

    deltaClassFromString (val) {
      if (!val) return ''
      if (val.toString().startsWith('+')) return 'red--text'
      if (val.toString().startsWith('-')) return 'green--text'
      return ''
    },

    // Method to refresh expanded data for a specific item
    refreshExpandedItem (item) {
      const expandedItem = this.expanded.find(i => i.unitId === item.unitId)
      if (expandedItem) {
        // Collapse and re-expand to refresh
        this.expanded = this.expanded.filter(i => i.unitId !== item.unitId)
        this.$nextTick(() => {
          this.expanded.push(item)
        })
      }
    },

    // Collapse all expanded rows
    collapseAll () {
      this.expanded = []
    },

    // Expand all rows (be careful with performance)
    expandAll () {
      this.expanded = [...this.localRows]
    },

    // Set mobile breakpoint programmatically
    setMobileBreakpoint (breakpoint) {
      this.localMobileBreakpoint = breakpoint
      this.mobileViewEnabled = false
      this.$emit('breakpoint-changed', breakpoint)
    },

    // Toggle between table and card view
    toggleTableView () {
      if (this.mobileViewEnabled) {
        this.mobileViewEnabled = false
      } else {
        this.mobileViewEnabled = true
      }
    }
  }
}
</script>

<style scoped>
.elevation-1 thead tr th {
  white-space: normal;
}

.sub-table-scroll {
  overflow-x: auto;
  width: 100%;
}

.status-col {
  width: 28px !important;
  max-width: 28px !important;
  min-width: 28px !important;
  padding: 0px 0px 0px 0px !important;
  overflow: hidden !important;
  white-space: nowrap !important;
  text-overflow: ellipsis !important;
}

.mobile-card {
  border-radius: 8px;
  transition: all 0.2s ease;
}

.mobile-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
</style>
